import { useState, useCallback, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ExtractionQuestion } from '@trinity/chat';
import { TagDetailsSection } from '../ProjectTags/TagDetails/TagDetailsSection';
import { TagDetailsSectionEditButtons } from '../ProjectTags/TagDetails/TagDetailsSectionEditButtons';
import { ExtractionQuestionRow } from './ExtractionQuestionRow';
import {
  ExtractionQuestionsFormSchema,
  type ExtractionQuestionsFormValues,
} from './extraction-questions-form.schema';

export type ExtractionQuestionsFormProps = {
  items: ExtractionQuestion[];
  onSave?: (questions: ExtractionQuestion[]) => Promise<void>;
  isSaving?: boolean;
};

function createEmptyQuestion(): ExtractionQuestion {
  return {
    answerType: 'free_text' as const,
    questionText: '',
    label: '',
  };
}

export const ExtractionQuestionsForm = ({
  items,
  onSave,
  isSaving = false,
}: ExtractionQuestionsFormProps) => {
  const [editing, setEditing] = useState(false);
  const submitRef = useRef<() => void>(() => {});

  const handleSave = useCallback(
    async (questions: ExtractionQuestion[]) => {
      if (!onSave) return;
      await onSave(questions);
      setEditing(false);
    },
    [onSave]
  );

  const handleCancel = useCallback(() => {
    setEditing(false);
  }, []);

  const editActions = onSave ? (
    <TagDetailsSectionEditButtons
      isEditing={editing}
      isSaving={isSaving}
      onEdit={() => setEditing(true)}
      onSave={() => submitRef.current()}
      onCancel={handleCancel}
    />
  ) : undefined;

  return (
    <TagDetailsSection title="Structured Extraction Configuration" actions={editActions}>
      <Description>
        Configure questions to automatically extract specific data points. Each question defines what to
        extract and how the answer should be structured.
      </Description>

      {!editing && <ExtractionQuestionsReadOnly items={items} />}
      {editing && (
        <ExtractionQuestionsEditor
          items={items}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
          submitRef={submitRef}
        />
      )}
    </TagDetailsSection>
  );
};

ExtractionQuestionsForm.displayName = 'ExtractionQuestionsForm';

// ─── Read-Only View ─────────────────────────────────────────────────────────

type ReadOnlyProps = {
  items: ExtractionQuestion[];
};

const ExtractionQuestionsReadOnly = ({ items }: ReadOnlyProps) => {
  if (items.length === 0) {
    return <EmptyState>No questions configured. Click Edit to add questions.</EmptyState>;
  }

  return (
    <QuestionsList>
      {items.map((question, index) => (
        <ExtractionQuestionRow
          key={index}
          question={question}
          onChange={() => {}}
          onRemove={() => {}}
          editing={false}
        />
      ))}
    </QuestionsList>
  );
};

// ─── Editor (mounts fresh, initializes from props) ──────────────────────────

type EditorProps = {
  items: ExtractionQuestion[];
  onSave: (questions: ExtractionQuestion[]) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  submitRef: React.RefObject<() => void>;
};

const ExtractionQuestionsEditor = ({ items, onSave, onCancel, isSaving, submitRef }: EditorProps) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ExtractionQuestionsFormValues>({
    resolver: zodResolver(ExtractionQuestionsFormSchema),
    defaultValues: {
      questions: items.length > 0 ? items : [createEmptyQuestion()],
    },
    mode: 'onSubmit',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });

  const handleAdd = useCallback(() => {
    append(createEmptyQuestion());
  }, [append]);

  const onSubmit = handleSubmit(async (data) => {
    await onSave(data.questions as ExtractionQuestion[]);
  });

  useEffect(() => {
    submitRef.current = onSubmit;
  }, [onSubmit, submitRef]);

  return (
    <>
      <QuestionsList>
        {fields.map((field, index) => (
          <ExtractionQuestionRow
            key={field.id}
            question={watch(`questions.${index}`) as ExtractionQuestion}
            onChange={(updated) => setValue(`questions.${index}`, updated, { shouldDirty: true })}
            onRemove={() => remove(index)}
            editing={true}
            errors={errors.questions?.[index]}
          />
        ))}
      </QuestionsList>

      {errors.questions?.root && <FormError>{errors.questions.root.message}</FormError>}

      <AddButton onClick={handleAdd} type="button">
        + Add Question
      </AddButton>
    </>
  );
};

// ─── Styled ─────────────────────────────────────────────────────────────────

const Description = styled.div`
  font-size: 14px;
  padding: 15px 0px 10px;
  color: var(--gray-d, #555);
`;

const EmptyState = styled.div`
  font-size: 14px;
  color: var(--gray-d, #888);
  padding: 20px 0;
  text-align: center;
  font-style: italic;
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
`;

const FormError = styled.div`
  font-size: 13px;
  color: var(--red, #d32f2f);
  margin-top: 8px;
`;

const AddButton = styled.button`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--pri-02);
  background: none;
  border: 1px dashed var(--pri-02);
  border-radius: 4px;
  cursor: pointer;
  padding: 10px 16px;
  margin-top: 12px;
  width: 100%;
  transition: background 0.15s ease;

  &:hover {
    background: var(--gray-xl, #f5f5f5);
  }
`;
